import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { CheckSquare, Plus, CalendarIcon, Pencil, Trash2, Loader2, Sun, CheckCircle, Globe } from "lucide-react";
import { formatSydney, formatTableDateSydney, getCurrentSydneyDisplay } from "../../utils/timezone";

export default function StaffTodos() {
  const { api } = useAuth();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  
  const [form, setForm] = useState({
    title: "",
    description: "",
    due_date: null,
    is_tomorrow: false,
  });

  const fetchTodos = async () => {
    try {
      const response = await api().get("/todos");
      setTodos(response.data);
    } catch (error) {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodos();
  }, []);

  const resetForm = () => {
    setForm({
      title: "",
      description: "",
      due_date: null,
      is_tomorrow: false,
    });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Please enter a task title");
      return;
    }

    setSaving(true);
    const payload = {
      title: form.title,
      description: form.description,
      due_date: form.due_date ? format(form.due_date, "yyyy-MM-dd") : null,
      is_tomorrow: form.is_tomorrow,
    };

    try {
      if (editingId) {
        await api().put(`/todos/${editingId}`, payload);
        toast.success("Task updated!");
      } else {
        await api().post("/todos", payload);
        toast.success("Task added!");
      }
      setDialogOpen(false);
      resetForm();
      fetchTodos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save task");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleComplete = async (todo) => {
    try {
      await api().put(`/todos/${todo.id}`, { is_completed: !todo.is_completed });
      fetchTodos();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleEdit = (todo) => {
    setForm({
      title: todo.title,
      description: todo.description || "",
      due_date: todo.due_date ? new Date(todo.due_date) : null,
      is_tomorrow: todo.is_tomorrow,
    });
    setEditingId(todo.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api().delete(`/todos/${id}`);
      toast.success("Task deleted");
      fetchTodos();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const pendingTodos = todos.filter(t => !t.is_completed);
  const completedTodos = todos.filter(t => t.is_completed);
  const tomorrowTodos = pendingTodos.filter(t => t.is_tomorrow);

  return (
    <div className="space-y-8" data-testid="staff-todos">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Tasks & Notes</h1>
          <p className="text-slate-500 mt-1">Manage your personal to-do list</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 btn-active" data-testid="add-task-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Task
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Task" : "Add New Task"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="What needs to be done?"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="task-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Add more details..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  data-testid="task-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Due Date (optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="task-due-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.due_date ? format(form.due_date, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.due_date}
                      onSelect={(date) => setForm({ ...form, due_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="tomorrow"
                  checked={form.is_tomorrow}
                  onCheckedChange={(checked) => setForm({ ...form, is_tomorrow: checked })}
                  data-testid="task-tomorrow-checkbox"
                />
                <Label htmlFor="tomorrow" className="text-sm font-normal cursor-pointer">
                  Add to tomorrow's plan
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={saving}
                data-testid="task-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : editingId ? "Update Task" : "Add Task"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tomorrow's Plan */}
      {tomorrowTodos.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <Sun className="h-5 w-5" />
              Tomorrow's Plan
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {tomorrowTodos.map((todo) => (
              <div
                key={todo.id}
                className="flex items-center gap-3 p-3 bg-white rounded-lg border border-amber-200"
                data-testid={`tomorrow-task-${todo.id}`}
              >
                <Checkbox
                  checked={todo.is_completed}
                  onCheckedChange={() => handleToggleComplete(todo)}
                />
                <span className="flex-1 text-sm font-medium text-slate-900">{todo.title}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <CheckSquare className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{pendingTodos.length}</p>
                <p className="text-sm text-slate-500">Pending Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-50 rounded-lg">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{completedTodos.length}</p>
                <p className="text-sm text-slate-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Tasks */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Active Tasks</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {pendingTodos.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CheckSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p>No pending tasks. Add one to get started!</p>
            </div>
          ) : (
            pendingTodos.filter(t => !t.is_tomorrow).map((todo, index) => (
              <div
                key={todo.id}
                className={`flex items-start gap-4 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors animate-fade-in stagger-${Math.min(index + 1, 6)}`}
                data-testid={`task-item-${todo.id}`}
              >
                <Checkbox
                  checked={todo.is_completed}
                  onCheckedChange={() => handleToggleComplete(todo)}
                  className="mt-1"
                  data-testid={`task-checkbox-${todo.id}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900">{todo.title}</p>
                  {todo.description && (
                    <p className="text-sm text-slate-500 mt-1">{todo.description}</p>
                  )}
                  {todo.due_date && (
                    <p className="text-xs text-slate-400 mt-2">
                      Due: {formatTableDateSydney(todo.due_date)}
                    </p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(todo)}
                    data-testid={`edit-task-${todo.id}`}
                  >
                    <Pencil className="h-4 w-4 text-slate-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(todo.id)}
                    data-testid={`delete-task-${todo.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Completed Tasks */}
      {completedTodos.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-500">Completed</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {completedTodos.map((todo) => (
              <div
                key={todo.id}
                className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg opacity-60"
                data-testid={`completed-task-${todo.id}`}
              >
                <Checkbox
                  checked={todo.is_completed}
                  onCheckedChange={() => handleToggleComplete(todo)}
                />
                <span className="flex-1 line-through text-slate-500">{todo.title}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(todo.id)}
                >
                  <Trash2 className="h-4 w-4 text-slate-400" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
